<template>
  <div class="bg-white-1 border-beige-10 flex flex-col gap-3 rounded border border-solid p-5">
    <a name="details">
      <Transition mode="out-in">
        <RfBaseSkeleton v-if="skeleton" height="23" width="189" srLabel="Loading details" />
        <h2 v-else class="text-heading-1 text-beige-90">Details</h2>
      </Transition>
    </a>

    <div class="flex flex-col gap-x-8 gap-y-6 lg:flex-row">
      <div
        class="lg:rf-prompt-details-img rf-prompt-details-img--small-screen flex! flex-col gap-3"
      >
        <div
          ref="imagePreviewRef"
          class="group/thumb rf-details--thumbnail relative origin-bottom-left translate-x-0 translate-y-0 rotate-0 skew-x-0 skew-y-0 overflow-hidden rounded!"
          :class="{ 'lg:scale-200 lg:rounded-none!': imagePreview }"
        >
          <Transition mode="out-in">
            <div v-if="skeleton" class="h-full w-full">
              <RfBaseSkeleton class="h-full! w-full!" radius="4" />
            </div>
            <template v-else>
              <RfPromotionThumbnail
                v-if="props.prompt?.actions"
                class="h-full w-full"
                :item="props.prompt"
              />
            </template>
          </Transition>
          <button
            v-if="!props.isInvisible"
            class="bg-black-3/0 group-hover/thumb:bg-black-3/60! absolute inset-0 flex!"
            :class="{ 'opacity-0': imagePreview }"
            @click="clickPreview"
          >
            <span
              class="m-auto inline-flex items-center gap-1 opacity-0 group-hover/thumb:opacity-100!"
            >
              <RfEyeOpenIcon class="fill-white-1! h-6! w-6!" />
              <span class="text-body text-white-1"> Preview </span>
            </span>
          </button>
        </div>
        <div class="inline-flex aspect-auto!">
          <Transition mode="out-in">
            <RfBaseSkeleton v-if="skeleton" class="w-full!" height="42" width="0" />
            <RfButtonNew
              v-else
              size="large"
              type="secondary"
              :disabled="
                props.isInvisible && 'Creative editor is not available for invisible prompt'
              "
              class="inline-grid w-full place-items-center p-2"
              :to="creativeLink"
            >
              <template v-if="props.isDisabledRole || props.isDisabledByActiveExperiment">
                <RfEyeOpenIcon class="h-6! w-6!" />
                View
              </template>
              <template v-else>
                <RfEditIcon class="h-6! w-6!" />
                Edit
              </template>
              prompt design
            </RfButtonNew>
          </Transition>
        </div>
        <div v-if="showFigmaImport || skeleton" class="inline-flex aspect-auto!">
          <Transition mode="out-in">
            <RfBaseSkeleton v-if="skeleton" class="w-full!" height="42" width="0" />
            <RfButtonNew
              v-else
              size="large"
              type="tertiary"
              :disabled="props.isDisabledRole || props.isDisabledByActiveExperiment"
              class="inline-flex w-full items-center justify-center gap-2 p-2"
              @click="emit('importFromFigma')"
            >
              <RfFigmaMonoIcon class="h-4! w-4! shrink-0" />
              Import Designs from Figma
            </RfButtonNew>
          </Transition>
        </div>
      </div>
      <div class="flex h-fit flex-1 flex-col overflow-clip">
        <RfTable
          class="w-full overflow-auto"
          :loading="skeleton || !props.prompt?.filter"
          :tableData="{ totalCount: 4, loadMore: 4 }"
          hideHeader
          :rowSkeleton="RfPromptDetailsRowSkeleton"
        >
          <template v-if="!skeleton" #tbody>
            <template v-for="row in detailsRow" :key="row.name">
              <tr
                v-if="row.showIf"
                class="border-strokes h-full border-t border-solid first:border-t-0"
              >
                <td class="text-heading-2 text-black-2 h-px! w-0 min-w-40 py-2! pl-0!">
                  <div class="flex h-full w-full items-start">
                    <div class="flex h-7 items-center">{{ row.name }}</div>
                  </div>
                </td>
                <td class="py-2!">
                  <component
                    :is="row.component"
                    v-if="row.component"
                    v-bind="{ ...row.props, ...row.listeners }"
                  />
                </td>
                <td class="w-0! pt-1.5! pb-1! align-top!">
                  <RfButtonNew
                    v-if="row.event"
                    size="none"
                    type="plain"
                    :disabled="
                      props.isDisabledRole ||
                      row.disableIf ||
                      (!!row.props.sequenceId && 'Disabled due to inheritance')
                    "
                    class="relative mt-1! h-6 w-6 before:absolute before:-inset-2"
                    @click="emit(row.event)"
                  >
                    <component :is="row.eventIcon || RfEditIcon" class="h-6! w-6!" />
                  </RfButtonNew>
                </td>
              </tr>
            </template>
          </template>
        </RfTable>
      </div>
    </div>
  </div>
</template>

<script setup>
import RfPromotionThumbnail from "@/components/RfPromotions/RfPromotionThumbnail.vue";
import RfTable from "@/blocks/RfTable/RfTable.vue";
import { computed, ref } from "vue";
import RfEditIcon from "@/components/icons/RfEditIcon.vue";
import { getSegmentLink, getZoneLink } from "@/utils/getLink";
import { percentage } from "@/utils/metricsHelpers";
import RfEyeOpenIcon from "@/components/icons/RfEyeOpenIcon.vue";
import { usePreviewToggle } from "@/utils/composables/usePreviewToggle";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import { isStandalone } from "@/utils/prompts/promptHelpers";
import { displayBucketRange } from "@/utils/segmentsHelpers";
import { DAYS, VISIT } from "@/utils/constants/FrequencyCapConstants";
import RfPromptDetailsSegments from "./RfPromptDetailsSegments.vue";
import RfPromptDetailsTriggers from "./RfPromptDetailsTriggers.vue";
import RfPromptDetailsLimits from "./RfPromptDetailsLimits.vue";
import RfPromptDetailsSchedule from "./RfPromptDetailsSchedule.vue";
import RfPromptDetailsRowSkeleton from "./RfPromptDetailsRowSkeleton.vue";
import RfPromptDetailsZone from "./RfPromptDetailsZone.vue";
import RfPromptDetailsInterval from "./RfPromptDetailsInterval.vue";
import RfPromptDetailsTags from "./RfPromptDetailsTags.vue";
import RfPromptDetailsMetadata from "./RfPromptDetailsMetadata.vue";
import RfPromptDetailsCategory from "./RfPromptDetailsCategory.vue";
import { GUIDE_TYPES } from "@/utils/constants/GuidesConstants";
import { getSubcategoryLabel } from "@/utils/constants/EngagementCategoryConstants";
import RfPromptDetailsText from "./RfPromptDetailsText.vue";
import RfCopyIcon from "@/components/icons/RfCopyIcon.vue";
import { useAppsStore } from "@/pinia/appsStore";
import { useEngagementCategories } from "@/utils/composables/useEngagementCategories";
import { useConsentCategories } from "@/utils/composables/useConsentCategories";
import { DeviceTypeWording } from "@/utils/constants/DevicesConstants";
import RfFigmaMonoIcon from "@/components/icons/RfFigmaMonoIcon.vue";
import { PromptTypes } from "@/utils/constants/PromoTypesConstants";

const emit = defineEmits([
  "editSegments",
  "editTriggers",
  "editLimits",
  "editSchedule",
  "editInterval",
  "editMetadata",
  "editEngagementCategory",
  "editConsentCategories",
  "edit",
  "createTag",
  "removeTag",
  "assignTag",
  "copyPromptId",
  "importFromFigma",
]);

const props = defineProps({
  prompt: { type: Object, default: () => ({}) },
  segments: { type: Array, default: () => [] },
  appTags: { type: Array, default: () => [] },
  promptTags: { type: Array, default: () => [] },
  appId: { type: String, default: "" },
  creativeLink: { type: String, default: null },
  guide: { type: Object, default: null },
  triggers: { type: Array, default: () => [] },
  isDisabledRole: { type: [Boolean, String], default: false },
  isDisabledByActiveExperiment: { type: [Boolean, String], default: false },
  isInvisible: { type: Boolean, default: false },
  isPushNotification: { type: Boolean, default: false },
  skeleton: { type: Boolean, default: false },
});

const imagePreviewRef = ref(null);
const imagePreview = ref(false);
const clickPreview = usePreviewToggle(imagePreviewRef, imagePreview);
const hasZone = computed(() => !isStandalone(props.prompt));

const { engagementCategoriesEnabled: showEngagementCategoryField } = useEngagementCategories();
const {
  consentCategoriesEnabled: showConsentCategoriesField,
  consentCategories: availableConsentCategories,
} = useConsentCategories();
const appsStore = useAppsStore();

const showFigmaImport = computed(
  () =>
    appsStore.app?.flags?.figma_import_enabled &&
    props.prompt?.device_type === DeviceTypeWording.web.value &&
    props.prompt?.path_type === PromptTypes.retention_modal.value &&
    appsStore.currAppConnectors?.figma?.status === "connected",
);

const detailsRow = computed(() => {
  return [
    {
      name: "Prompt ID",
      component: RfPromptDetailsText,
      showIf: true,
      props: { text: props.prompt.id },
      event: "copyPromptId",
      eventIcon: RfCopyIcon,
    },
    {
      name: "Created by",
      component: RfPromptDetailsText,
      showIf: true,
      props: { text: props.prompt.creator || "Recurly" },
    },
    {
      name: "Engagement Category",
      component: RfPromptDetailsCategory,
      showIf: showEngagementCategoryField.value,
      event: props.prompt.sequence_id ? null : "editEngagementCategory",
      props: {
        category: props.prompt.engagement_category,
        ...(props.guide && { inherited: props.guide }),
      },
    },
    {
      name: "Engagement Subcategory",
      component: RfPromptDetailsText,
      showIf: !!props.prompt.engagement_subcategory,
      props: { text: getSubcategoryLabel(props.prompt.engagement_subcategory) },
    },
    {
      name: "Consent Categories",
      component: RfPromptDetailsText,
      showIf: showConsentCategoriesField.value,
      event: "editConsentCategories",
      props: {
        text: props.prompt.consent_categories?.length
          ? props.prompt.consent_categories
              .map(v => availableConsentCategories.value.find(c => c.value === v)?.label ?? v)
              .join(", ")
          : null,
      },
    },
    {
      name: "Zone",
      component: RfPromptDetailsZone,
      showIf: hasZone.value,
      disableIf: props.isDisabledByActiveExperiment,
      props: {
        zone: {
          name: props.prompt.path_group?.name || "Default",
          link: getZoneLink(props.prompt.path_group.id, props.appId),
        },
      },
    },
    {
      name: "Segments",
      event: "editSegments",
      component: RfPromptDetailsSegments,
      showIf: true,
      disableIf: props.isDisabledByActiveExperiment,
      props: {
        links: props.segments.map(seg => ({
          name: seg.name,
          bucketRange: displayBucketRange(seg),
          link: getSegmentLink(seg.id, props.appId),
        })),
        ...(props.guide && { inherited: props.guide }),
      },
    },
    {
      name: "Tags",
      component: RfPromptDetailsTags,
      showIf: true,
      disableIf: props.isDisabledByActiveExperiment,
      props: {
        appTags: props.appTags,
        appId: props.appId,
        promptTags: props.promptTags,
        disabled: props.isDisabledRole || props.isDisabledByActiveExperiment,
      },
      listeners: {
        onCreateTag: tag => emit("createTag", tag),
        onRemoveTag: tag => emit("removeTag", tag),
        onAssignTag: tag => emit("assignTag", tag),
      },
    },
    {
      name: "Triggers",
      event: "editTriggers",
      showIf: props.prompt.path_type !== PromptTypes.scheduled_push.value,
      disableIf: props.isDisabledByActiveExperiment,
      component: RfPromptDetailsTriggers,
      props: {
        triggers: props.triggers,
        ...(props.guide &&
          props.guide.type === GUIDE_TYPES.web_client.value &&
          props.guide.order !== 0 && {
            inherited: props.guide,
          }),
      },
    },
    {
      name: "Limits",
      event: "editLimits",
      component: RfPromptDetailsLimits,
      showIf: true,
      disableIf: props.isDisabledByActiveExperiment,
      props: {
        limits: {
          limit: {
            if: props.prompt.filter.traffic.is_limited,
            curr: props.prompt.filter.traffic.limit - (props.prompt.curr_limit || 0),
          },
          cap: {
            if: props.prompt.filter.frequency_cap.is_capped,
            value: props.prompt.filter.frequency_cap.cap,
            type:
              props.prompt.filter.frequency_cap.cap_seconds === VISIT.value
                ? VISIT.value
                : DAYS.value,
            days: Math.floor(props.prompt.filter.frequency_cap.cap_seconds / (3600 * 24)),
          },
          budget: {
            if: props.prompt.filter.supply.is_budgeted,
            curr: props.prompt.filter.supply.budget - (props.prompt.curr_budget || 0),
            percent:
              100 -
              percentage(
                parseInt(props.prompt.curr_budget),
                parseInt(props.prompt.filter.supply.budget),
              ),
          },
          user: {
            if: props.prompt.filter.users.is_limited,
            curr: props.prompt.filter.users.limit - (props.prompt.curr_users || 0),
          },
          delivery: {
            if: props.prompt.filter.deliveries.is_limited,
            curr: props.prompt.filter.deliveries.limit - (props.prompt.curr_deliveries || 0),
          },
          pushLimits: {
            if:
              props.prompt.filter.push_limits?.is_daily_capped ||
              props.prompt.filter.push_limits?.is_total_capped,
            dailyCap: props.prompt.filter.push_limits?.daily_cap,
            totalCap: props.prompt.filter.push_limits?.total_cap,
          },
        },
        ...(props.guide && { inherited: props.guide }),
      },
    },
    {
      name: "Schedule",
      event: "editSchedule",
      component: RfPromptDetailsSchedule,
      showIf: true,
      disableIf: false,
      props: {
        schedule: {
          startDate: props.prompt.start_date,
          endDate: props.prompt.end_date,
          daypart: props.prompt.filter.schedule,
        },
        isPushNotification: props.isPushNotification,
        actions: props.prompt.actions,
        ...(props.guide && { inherited: props.guide }),
      },
    },
    {
      name: "Interval",
      event: "editInterval",
      component: RfPromptDetailsInterval,
      showIf: props.isInvisible,
      disableIf: props.isDisabledByActiveExperiment,
      props: {
        display: props.prompt.actions.rf_settings_permanent_display === "true",
        interval: props.prompt.actions.rf_settings_accept_interval,
      },
    },
    {
      name: "Metadata",
      event: "editMetadata",
      component: RfPromptDetailsMetadata,
      showIf: props.isInvisible,
      disableIf: props.isDisabledByActiveExperiment,
      props: { metadata: props.prompt.actions?.rf_metadata },
    },
  ];
});
</script>

<style scoped>
.rf-details--thumbnail {
  transition: transform 300ms cubic-bezier(0.5, 1.2, 0.5, 1.2);
}
</style>

<style>
.rf-details--thumbnail.lg\:scale-200 {
  .promo-preview-image {
    > div {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    img {
      object-fit: contain;
      max-height: 100%;
    }
  }
}
</style>
